import type { AIModelInfo } from '$lib/types/ai';

// Asset imports
import openaiIcon from '$lib/../assets/ai-model-icons/openai.png';
import claudeIcon from '$lib/../assets/ai-model-icons/claude.png';
import googleIcon from '$lib/../assets/ai-model-icons/google.png';
import openrouterIcon from '$lib/../assets/ai-model-icons/openrouter.png';
import upstageIcon from '$lib/../assets/ai-model-icons/upstage.png';

/**
 * AI Model Configuration
 * Centralized definition of all available AI models and their capabilities
 */
export const AI_MODELS: Record<string, AIModelInfo[]> = {
  openai: [
    {
      id: 'gpt-4o',
      name: 'GPT-4o',
      provider: 'openai',
      icon: openaiIcon,
      description: 'Most capable GPT-4 model with vision support',
      maxTokens: 128000,
      costPerToken: 0.00005,
      capabilities: {
        streaming: true,
        vision: true,
        functionCalling: true,
        jsonMode: true
      },
      performance: {
        speed: 'medium',
        accuracy: 'very high',
        contextWindow: 128000
      }
    },
    {
      id: 'gpt-4o-mini',
      name: 'GPT-4o Mini',
      provider: 'openai',
      icon: openaiIcon,
      description: 'Faster, more affordable GPT-4 variant',
      maxTokens: 128000,
      costPerToken: 0.00015,
      recommended: true,
      capabilities: {
        streaming: true,
        vision: true,
        functionCalling: true,
        jsonMode: true
      },
      performance: {
        speed: 'fast',
        accuracy: 'high',
        contextWindow: 128000
      }
    },
    {
      id: 'gpt-4-turbo',
      name: 'GPT-4 Turbo',
      provider: 'openai',
      icon: openaiIcon,
      description: 'Latest GPT-4 with improved performance',
      maxTokens: 128000,
      costPerToken: 0.00003,
      capabilities: {
        streaming: true,
        vision: true,
        functionCalling: true,
        jsonMode: true
      },
      performance: {
        speed: 'medium',
        accuracy: 'very high',
        contextWindow: 128000
      }
    }
  ],
  
  anthropic: [
    {
      id: 'claude-3-5-sonnet-20241022',
      name: 'Claude 3.5 Sonnet',
      provider: 'anthropic',
      icon: claudeIcon,
      description: 'Anthropic\'s most intelligent model',
      maxTokens: 200000,
      costPerToken: 0.00003,
      capabilities: {
        streaming: true,
        vision: true,
        functionCalling: false,
        jsonMode: false
      },
      performance: {
        speed: 'fast',
        accuracy: 'exceptional',
        contextWindow: 200000
      }
    },
    {
      id: 'claude-3-5-haiku-20241022',
      name: 'Claude 3.5 Haiku',
      provider: 'anthropic',
      icon: claudeIcon,
      description: 'Fast and efficient for everyday tasks',
      maxTokens: 200000,
      costPerToken: 0.00025,
      recommended: true,
      capabilities: {
        streaming: true,
        vision: false,
        functionCalling: false,
        jsonMode: false
      },
      performance: {
        speed: 'very fast',
        accuracy: 'good',
        contextWindow: 200000
      }
    },
    {
      id: 'claude-3-opus-20240229',
      name: 'Claude 3 Opus',
      provider: 'anthropic',
      icon: claudeIcon,
      description: 'Most capable model for complex tasks',
      maxTokens: 200000,
      costPerToken: 0.000015,
      capabilities: {
        streaming: true,
        vision: true,
        functionCalling: false,
        jsonMode: false
      },
      performance: {
        speed: 'slow',
        accuracy: 'exceptional',
        contextWindow: 200000
      }
    }
  ],
  
  google: [
    {
      id: 'models/gemini-2.5-pro',
      name: 'Gemini 2.5 Pro',
      provider: 'google',
      icon: googleIcon,
      description: '향상된 사고 및 추론, 멀티모달 이해, 고급 코딩',
      maxTokens: 2000000,
      costPerToken: 0.0000125,
      capabilities: {
        streaming: true,
        vision: true,
        functionCalling: true,
        jsonMode: false
      },
      performance: {
        speed: 'medium',
        accuracy: 'exceptional',
        contextWindow: 2000000
      }
    },
    {
      id: 'models/gemini-2.5-flash',
      name: 'Gemini 2.5 Flash',
      provider: 'google',
      icon: googleIcon,
      description: '적응적 사고, 비용 효율성',
      maxTokens: 1000000,
      costPerToken: 0.00000075,
      recommended: true,
      capabilities: {
        streaming: true,
        vision: true,
        functionCalling: true,
        jsonMode: false
      },
      performance: {
        speed: 'very fast',
        accuracy: 'high',
        contextWindow: 1000000
      },
      thinkingBudget: 0
    },
    {
      id: 'models/gemini-2.5-flash-lite',
      name: 'Gemini 2.5 Flash-Lite',
      provider: 'google',
      icon: googleIcon,
      description: '대용량 작업을 지원하는 가장 비용 효율적인 모델',
      maxTokens: 1000000,
      costPerToken: 0.0000005,
      recommended: true,
      capabilities: {
        streaming: true,
        vision: true,
        functionCalling: true,
        jsonMode: false
      },
      performance: {
        speed: 'very fast',
        accuracy: 'good',
        contextWindow: 1000000
      },
      thinkingBudget: 0
    }
  ],
  
  upstage: [
    {
      id: 'solar-1-mini-chat',
      name: 'SOLAR 1 Mini',
      provider: 'upstage',
      icon: upstageIcon,
      description: 'Efficient Korean-optimized model',
      maxTokens: 32000,
      costPerToken: 0.00001,
      recommended: true,
      capabilities: {
        streaming: false,
        vision: false,
        functionCalling: false,
        jsonMode: false
      },
      performance: {
        speed: 'fast',
        accuracy: 'good',
        contextWindow: 32000,
        koreanOptimized: true
      }
    },
    {
      id: 'solar-pro',
      name: 'SOLAR Pro',
      provider: 'upstage',
      icon: upstageIcon,
      description: 'Advanced Korean language model',
      maxTokens: 4000,
      costPerToken: 0.00002,
      capabilities: {
        streaming: false,
        vision: false,
        functionCalling: false,
        jsonMode: false
      },
      performance: {
        speed: 'medium',
        accuracy: 'high',
        contextWindow: 4000,
        koreanOptimized: true
      }
    }
  ],
  
  openrouter: [
    {
      id: 'openai/gpt-oss-120b',
      name: 'GPT-OSS 120B',
      provider: 'openrouter',
      icon: openrouterIcon,
      description: 'Free open-source model via OpenRouter',
      maxTokens: 128000,
      costPerToken: 0,  // Free model
      recommended: true,
      capabilities: {
        streaming: true,
        vision: false,
        functionCalling: false,
        jsonMode: false
      },
      performance: {
        speed: 'fast',
        accuracy: 'high',
        contextWindow: 128000
      }
    },
    {
      id: 'openai/qwen3-32b',
      name: 'Qwen3 32B',
      provider: 'openrouter',
      icon: openrouterIcon,
      description: 'Free Qwen model via OpenRouter',
      maxTokens: 32768,
      costPerToken: 0,  // Free model
      recommended: true,
      capabilities: {
        streaming: true,
        vision: false,
        functionCalling: false,
        jsonMode: false
      },
      performance: {
        speed: 'fast',
        accuracy: 'high',
        contextWindow: 32768
      }
    }
  ]
};

/**
 * Provider display names and metadata
 */
export const PROVIDER_INFO: Record<string, {
  name: string;
  description: string;
  requiresApiKey: boolean;
  supportedFeatures: string[];
}> = {
  openai: {
    name: 'OpenAI',
    description: 'GPT models with advanced capabilities',
    requiresApiKey: true,
    supportedFeatures: ['streaming', 'vision', 'function-calling', 'json-mode']
  },
  anthropic: {
    name: 'Anthropic',
    description: 'Claude models with exceptional reasoning',
    requiresApiKey: true,
    supportedFeatures: ['streaming', 'vision', 'large-context']
  },
  google: {
    name: 'Google',
    description: 'Gemini models with massive context windows',
    requiresApiKey: true,
    supportedFeatures: ['streaming', 'vision', 'function-calling', 'large-context']
  },
  upstage: {
    name: 'Upstage',
    description: 'Korean-optimized SOLAR models',
    requiresApiKey: true,
    supportedFeatures: ['korean-optimization']
  },
  openrouter: {
    name: 'OpenRouter',
    description: 'Access multiple models through one API',
    requiresApiKey: false,
    supportedFeatures: ['streaming', 'vision', 'multi-provider']
  }
};

/**
 * Default model selections per provider
 */
export const DEFAULT_MODELS: Record<string, string> = {
  openai: 'gpt-4o-mini',
  anthropic: 'claude-3-5-sonnet-20241022',
  google: 'models/gemini-2.5-pro',
  upstage: 'solar-1-mini-chat',
  openrouter: 'anthropic/claude-3.5-sonnet'
};

/**
 * Model capability tags for filtering
 */
export const CAPABILITY_TAGS = {
  streaming: 'Streaming',
  vision: 'Vision',
  functionCalling: 'Functions',
  jsonMode: 'JSON Mode',
  koreanOptimized: 'Korean',
  largeContext: 'Large Context'
} as const;

/**
 * Price tier categories
 */
export const PRICE_TIERS = {
  free: { max: 0, label: 'Free', color: 'green' },
  cheap: { max: 0.00001, label: 'Budget', color: 'blue' },
  moderate: { max: 0.00005, label: 'Standard', color: 'yellow' },
  expensive: { max: 0.0001, label: 'Premium', color: 'orange' },
  veryExpensive: { max: Infinity, label: 'Enterprise', color: 'red' }
} as const;